import { Injectable, Pipe, PipeTransform, inject, signal } from '@angular/core';
import { en } from './i18n/en';
import { mk } from './i18n/mk';
import { sq } from './i18n/sq';

export type Lang = 'mk' | 'en' | 'sq';
export const LANGS: Array<{ code: Lang; label: string }> = [
  { code: 'mk', label: 'Македонски' },
  { code: 'en', label: 'English' },
  { code: 'sq', label: 'Shqip' },
];

const DICTS: Record<Lang, Record<string, string>> = { mk, en, sq };
const MONTHS: Record<Lang, string[]> = {
  mk: ['јан', 'фев', 'мар', 'апр', 'мај', 'јун', 'јул', 'авг', 'сеп', 'окт', 'ное', 'дек'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  sq: ['Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer', 'Korr', 'Gush', 'Sht', 'Tet', 'Nën', 'Dhj'],
};
const KEY = 'so.lang';

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY) as Lang | null;
    if (saved && saved in DICTS) return saved;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || 'mk').slice(0, 2);
  return nav === 'en' ? 'en' : nav === 'sq' ? 'sq' : 'mk';
}

/** Runtime UI translation (MK / EN / SQ); the language is also sent to the API. */
@Injectable({ providedIn: 'root' })
export class I18nService {
  readonly lang = signal<Lang>(initialLang());

  constructor() {
    document.documentElement.lang = this.lang();
  }

  set(lang: Lang) {
    this.lang.set(lang);
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(KEY, lang);
    } catch {
      /* ignore */
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    let text = DICTS[this.lang()][key] ?? DICTS.mk[key] ?? key;
    for (const [k, v] of Object.entries(params ?? {})) text = text.replace(`{${k}}`, String(v));
    return text;
  }

  get locale(): string {
    return { mk: 'mk-MK', en: 'en-GB', sq: 'sq-AL' }[this.lang()];
  }

  /** dd.MM.yyyy [HH:mm] in Europe/Skopje, independent of the browser's ICU data. */
  date(value: string | Date | null | undefined, withTime = false): string {
    if (!value) return '–';
    const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
    if (Number.isNaN(d.getTime())) return '–';
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Skopje',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(d)
        .map((p) => [p.type, p.value]),
    );
    const sep = this.lang() === 'en' ? '/' : '.';
    const date = `${parts['day']}${sep}${parts['month']}${sep}${parts['year']}`;
    return withTime ? `${date} ${parts['hour']}:${parts['minute']}` : date;
  }

  monthShort(month: number): string {
    return MONTHS[this.lang()][month] ?? String(month + 1);
  }

  number(value: number | null | undefined, digits = 0): string {
    if (value === null || value === undefined) return '–';
    return new Intl.NumberFormat(this.locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
  }
}

@Pipe({ name: 't', pure: false })
export class TranslatePipe implements PipeTransform {
  private readonly i18n = inject(I18nService);
  transform(key: string, params?: Record<string, string | number>): string {
    return this.i18n.t(key, params);
  }
}

@Pipe({ name: 'appDate', pure: false })
export class AppDatePipe implements PipeTransform {
  private readonly i18n = inject(I18nService);
  transform(value: string | Date | null | undefined, withTime = false): string {
    return this.i18n.date(value, withTime);
  }
}

@Pipe({ name: 'num', pure: false })
export class NumPipe implements PipeTransform {
  private readonly i18n = inject(I18nService);
  transform(value: number | null | undefined, digits = 0): string {
    return this.i18n.number(value, digits);
  }
}
